import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

/**
 * One `applicants_changed` signal from the per-job SSE stream (#3316, BE
 * ourteam-backend#4092). Signals carry no applicant data — the board refetches
 * the list on receipt — so unknown `change` values are fine to ignore.
 */
export interface ApplicantChangeEvent {
    job_id?: string;
    applicant_id?: string;
    change?: string;
    at?: string;
}

interface SubscribeOptions {
    /** Abort to tear the stream down (unmount / job switch). */
    signal: AbortSignal;
    /** Called once per received event frame. */
    onEvent: (event: ApplicantChangeEvent) => void;
    /** Called after a successful RE-connect — refetch to cover missed events. */
    onReconnect?: () => void;
}

/**
 * Subscribe to `GET /jobs/{job_id}/applicants/events` (SSE) via fetch +
 * ReadableStream — EventSource can't send the `Authorization: Bearer` header.
 *
 * Owns the whole lifecycle so the caller never has to handle errors:
 * - 401/403/404 (endpoint absent / not allowed) → log once and stop for good;
 *   the board keeps today's manual-refresh behavior.
 * - Network drops / stream end → reconnect with capped exponential backoff
 *   (1s → 30s), firing `onReconnect` on success.
 * - Heartbeat comments (`: ping`) and non-data lines are ignored.
 */
export function subscribeApplicantEvents(jobId: string, { signal, onEvent, onReconnect }: SubscribeOptions): void {
    let attempt = 0;
    const run = async () => {
        while (!signal.aborted) {
            try {
                const token = getCookie("authToken");
                const res = await fetch(`${apiURL}/jobs/${jobId}/applicants/events`, {
                    headers: {
                        Accept: "text/event-stream",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    signal,
                });
                if ([401, 403, 404].includes(res.status)) {
                    // Degrade silently and permanently — the endpoint isn't
                    // available (older BE) or this HR may not subscribe.
                    catchErrorLog(`[applicant-events] stream unavailable (HTTP ${res.status}) — staying on manual refresh`);
                    return;
                }
                if (!res.ok || !res.body) throw new Error(`SSE HTTP ${res.status}`);
                if (attempt > 0) onReconnect?.();
                attempt = 0;

                const reader = res.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    // SSE frames are separated by a blank line.
                    let sep;
                    while ((sep = buffer.indexOf("\n\n")) !== -1) {
                        const frame = buffer.slice(0, sep);
                        buffer = buffer.slice(sep + 2);
                        for (const rawLine of frame.split("\n")) {
                            const line = rawLine.replace(/\r$/, "");
                            if (!line.startsWith("data:")) continue;
                            try {
                                onEvent(JSON.parse(line.slice(5).trim()) as ApplicantChangeEvent);
                            } catch {
                                // Malformed payload — the signal itself is
                                // what matters, still trigger a refetch.
                                onEvent({});
                            }
                        }
                    }
                }
                // Server closed the stream cleanly — treat as a drop and reconnect.
                throw new Error("SSE stream ended");
            } catch {
                if (signal.aborted) return;
                attempt += 1;
                const delay = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5));
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }
    };
    void run();
}
